import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SearchModal({ products = [], textColor = 'text-black' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // ✅ Open modal and focus input
  const openModal = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ✅ Close modal
  const closeModal = () => {
    setIsOpen(false);
    setQuery('');
    setSuggestions([]);
  };

  // ✅ Close on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ✅ Search logic
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    const searchTerm = query.toLowerCase().trim();
    
    const results = products
      .filter(product => {
        const name = (product.name || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        const category = (product.category || '').toLowerCase();
        const sku = (product.sku || '').toLowerCase();
        
        let colors = [];
        try {
          colors = Array.isArray(product.color) 
            ? product.color 
            : JSON.parse(product.color || '[]');
        } catch {}
        const colorMatch = colors.some(c => 
          c.toLowerCase().includes(searchTerm)
        );
        
        return (
          name.includes(searchTerm) ||
          description.includes(searchTerm) ||
          category.includes(searchTerm) ||
          sku.includes(searchTerm) ||
          colorMatch
        );
      })
      .slice(0, 6);

    setSuggestions(results);
  }, [query, products]);

  // ✅ Handle search submit
// SearchModal.jsx дотор
  const handleSearch = (e) => {
    e.preventDefault();
    // Юу ч хийхгүй - зөвхөн dropdown ашиглана
  };

  // ✅ Handle suggestion click
  const handleSuggestionClick = (productId) => {
    navigate(`/product/${productId}`);
    closeModal();
  };

  // ✅ Get main image
  const getMainImage = (product) => {
    if (product.mainImage) {
      return `https://bodiosyakdown.com/uploads/${product.mainImage}`;
    }
    try {
      const images = JSON.parse(product.images || '[]');
      if (images.length > 0) {
        return `https://bodiosyakdown.com/uploads/${images[0]}`;
      }
    } catch {}
    return 'https://via.placeholder.com/80';
  };

  return (
    <>
      {/* Search Button */}
      <button
        onClick={openModal}
        className={`text-[16px] font-light uppercase tracking-wide hover:opacity-70 ${textColor}`}
        style={{ fontFamily: 'inherit' }}
        type="button"
      >
        SEARCH
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20"
          onClick={closeModal}
        >
          {/* Modal Content */}
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-2xl rounded-lg shadow-2xl animate-slideDown"
            style={{ animation: 'slideDown 0.3s ease-out' }}
          >
            {/* Search Input */}
            <form onSubmit={handleSearch} className="p-6 border-b">
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full px-4 py-3 pr-10 text-lg text-black border-0 focus:outline-none focus:ring-0"
                  style={{ fontFamily: 'inherit' }}
                />
                
                {/* Search Icon */}
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-black transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
              </div>

              {/* Close hint */}
              <p className="text-xs text-gray-400 mt-2">
                Press ESC to close
              </p>
            </form>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto">
              {query.trim() && suggestions.length === 0 ? (
                <div className="p-12 text-center">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-gray-600">No results found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try different keywords
                  </p>
                </div>
              ) : suggestions.length > 0 ? (
                <>
                  {suggestions.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleSuggestionClick(product.id)}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 transition-colors"
                    >
                      {/* Image */}
                      <img
                        src={getMainImage(product)}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ fontFamily: 'inherit' }}>
                          {product.name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {product.category}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="font-semibold">
                          ${Number(product.price || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* View All */}
                  <button
                    onClick={handleSearch}
                    className="w-full p-4 text-center text-sm text-gray-600 hover:bg-gray-50 font-medium border-t"
                  >
                    View all results
                  </button>
                </>
              ) : (
                <div className="p-12 text-center text-gray-400 text-sm">
                  Start typing to search...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}